/**
 * Proposing a footprint for a part that names none.
 *
 * The Footprint Check could always *search* the libraries, but you had to know
 * what to type — which means knowing that a 14-pin logic chip wants `DIP14`,
 * and that the library spells it that way. For anyone who has not used CIRCAD
 * for twenty years that is a dead end, and it is the single reason a sheet
 * transfers to a board and places nothing.
 *
 * So: given the package and the catalogue, rank the packages it could be. Two
 * facts do nearly all the work.
 *
 * **The pin count.** A package brings a known number of pins to the board and
 * a footprint has a known number of pads. They must agree, so anything that
 * disagrees is not a candidate at all — that one rule takes a hundred-odd
 * footprints down to a handful.
 *
 * **What the part calls itself.** `ATMEGA328P-PU` is a DIP and `ATMEGA328P-AU`
 * is a TQFP; `74HC04N` is a DIP and `74HC04D` is an SOIC. Manufacturers encode
 * the package in the ordering code, and while the conventions are not
 * universal they are consistent enough within a maker's range to be worth
 * reading — as a *hint* that ranks, never as a rule that excludes.
 *
 * Nothing here writes anything. It returns an ordered list with a reason
 * against each, because a suggestion the operator cannot check is a suggestion
 * they should not take.
 */

import type { FootprintNeed } from './footprints.ts';

/** A footprint the catalogue could offer. */
export interface FootprintCandidate {
  name: string;
  description: string;
  library: string;
  /** Pads. Null when the catalogue does not say, which costs it dearly. */
  pins: number | null;
  /** Other names this footprint answers to, e.g. `0805` for `R0805`. */
  aliases?: readonly string[];
  /** `DIP`, `SOIC`, `chip`, `TO`, … Null when unknown. */
  family: string | null;
  smt: boolean | null;
}

export interface Suggestion extends FootprintCandidate {
  score: number;
  /** Why this is being offered, in words the operator can check. */
  why: string;
}

/**
 * Package families named outright in a part number.
 *
 * Ordered longest-first where one contains another, so `TSSOP` is not read as
 * `SSOP` and `TQFP` is not read as `QFP`.
 */
const SPELT_OUT: [RegExp, string][] = [
  [/\bTSSOP\b/i, 'TSSOP'],
  [/\bSSOP\b/i, 'SSOP'],
  [/\b[TLP]?QFP\b/i, 'QFP'],
  [/\bPLCC\b/i, 'PLCC'],
  [/\bP?DIP\b/i, 'DIP'],
  [/\bSOIC\b|\bSOP\b|\bSO-?\d/i, 'SOIC'],
  [/\bSOT-?\d/i, 'SOT'],
  [/\bTO-?\d/i, 'TO'],
  [/\bSIP\b/i, 'SIP'],
  [/\bIDC\b/i, 'IDC'],
  [/\bDB-?\d/i, 'D-sub'],
  [/\b(0402|0603|0805|1206|1210|2010|2512|1812)\b/, 'chip'],
];

/**
 * Package letters at the end of an ordering code.
 *
 * TI, Atmel and ON all use something like this and they broadly agree; it is a
 * weaker signal than the family spelt out and is scored as one.
 */
const SUFFIX: [RegExp, string][] = [
  [/-?PU$|-?PI$|[^A-Z]N$|^.*[0-9]N$|-?P$/i, 'DIP'],
  [/-?AU$|-?AI$/i, 'QFP'],
  [/-?PW$|-?PWR$/i, 'TSSOP'],
  [/-?DW$|-?DWR$/i, 'SOIC'],
  [/[0-9]D$|-?DR$/i, 'SOIC'],
  [/-?FN$/i, 'PLCC'],
  [/-?DB$/i, 'SSOP'],
];

/** A pattern that names nothing in particular. */
export function isWildcard(name: string | null | undefined): boolean {
  if (!name) return true;
  const n = name.trim();
  return n === '' || n === '?' || n === '*' || /[*?]/.test(n);
}

/**
 * Does this look like an ordering code at all?
 *
 * The suffix rules read the last letters of a manufacturer's part number, and
 * a component *value* is not one: `100n` ends in N and is a hundred nanofarads,
 * not a plastic DIP. Two letters is the cheap test that separates `SN74HC04N`
 * and `ATMEGA328P-PU` from `10k`, `22p`, `1u` and `100n`.
 */
function looksLikeOrderingCode(part: string): boolean {
  return /[A-Za-z].*[A-Za-z]/.test(part);
}

/** The family a part number points at, and how loudly. */
export function familyHint(part: string): { family: string; strong: boolean } | null {
  for (const [re, family] of SPELT_OUT) {
    if (re.test(part)) return { family, strong: true };
  }
  if (!looksLikeOrderingCode(part)) return null;
  for (const [re, family] of SUFFIX) {
    if (re.test(part)) return { family, strong: false };
  }
  return null;
}

/**
 * Rank the footprints a package could take.
 *
 * The pin count is a filter and not a score: a footprint with the wrong number
 * of pads is not a worse answer, it is the wrong answer, and offering it
 * invites somebody to accept it. A candidate whose pad count the catalogue
 * does not know is kept, at the back, because that is a gap in the catalogue
 * rather than evidence about the part.
 */
export function suggestFootprints(
  need: Pick<FootprintNeed, 'part' | 'pins'> & { ref?: string },
  candidates: readonly FootprintCandidate[],
  limit = 8,
): Suggestion[] {
  const hint = familyHint(need.part);
  const kind = designatorKind(need.ref ?? '');
  const part = need.part.toUpperCase();
  const out: Suggestion[] = [];
  /** Where each candidate came in the catalogue, for the final tie-break. */
  const order = new Map<string, number>();

  for (const c of candidates) {
    if (c.pins !== null && need.pins > 0 && c.pins !== need.pins) continue;

    let score = 0;
    const why: string[] = [];

    if (c.pins !== null && c.pins === need.pins) {
      score += 100;
      why.push(`${c.pins} pads for ${need.pins} pins`);
    } else if (c.pins === null) {
      score -= 20;
      why.push('pad count not recorded');
    }

    if (hint && c.family && c.family.toUpperCase() === hint.family.toUpperCase()) {
      score += hint.strong ? 40 : 15;
      why.push(hint.strong
        ? `the part says ${hint.family}`
        : `the ordering code ends like a ${hint.family}`);
    }

    // The package named outright — `DIP-14` inside `74LS14 DIP-14`, or by one
    // of the names it answers to: a part called `RESISTOR 0805` is naming
    // `R0805` even though it does not spell it that way.
    const spoken = [c.name, ...(c.aliases ?? [])]
      .find((n) => n.length >= 3 && part.includes(n.toUpperCase()));
    if (spoken) {
      score += 25;
      why.push(spoken === c.name ? 'named in the part' : `the part says ${spoken}`);
    }

    // With nothing to go on, a through-hole package is the safer offer for a
    // catalogue whose symbols are overwhelmingly DIP-era parts. A nudge, not a
    // decision: the surface-mount equivalents are still in the list.
    if (!hint && c.smt === false) {
      score += 6;
      why.push('through hole');
    }

    // A three-pin part is far more likely to be a transistor than a three-way
    // header, and with no hint at all the two score identically. Devices before
    // connectors, so `BC547` is offered TO-92 rather than SIP-3.
    if (!hint) score += DEVICE_FIRST.has(c.family ?? '') ? 4 : 0;

    // What the part is called on the sheet.
    //
    // `C4` is a capacitor and `R1` is a resistor, and the pin count alone
    // cannot tell them apart — both have two pads, so without this a decoupling
    // capacitor was offered `SMA`, `SMB` and `TO-3`, which are a rectifier, a
    // rectifier and a power transistor. The designator is the one piece of
    // evidence on the sheet that says what kind of thing this is, and every
    // schematic carries it.
    if (kind && kind.families.includes(c.family ?? '')) {
      score += 30;
      why.push(`${need.ref} is ${kind.what}`);
    }

    out.push({ ...c, score, why: why.join(', ') });
    order.set(c.name, order.size);
  }

  // Plainest name wins a tie, so `DIP-24` is offered before `DIP-24N`. Beyond
  // that, the order the catalogue lists them in — which is curated, and is the
  // only thing left that carries any judgement. `TO-92` and `TO-39` take three
  // pins each, score identically and are the same length; one of them is what
  // a small-signal transistor is in and the other is a metal can from 1970,
  // and alphabetical order picks the can.
  out.sort((a, b) => b.score - a.score
    || a.name.length - b.name.length
    || (order.get(a.name) ?? 0) - (order.get(b.name) ?? 0));

  // At most two of any one family.
  //
  // Six TO packages all take three pins and all score the same, so without
  // this a three-pin part is offered TO-92, TO-220, TO-126, TO-39, TO-218 and
  // TO-3 — six ways of saying the same thing, and no SOT-23. The operator is
  // choosing between *kinds* of package first and sizes within a kind second,
  // so the list should be a spread rather than a run.
  const perFamily = new Map<string, number>();
  const spread: Suggestion[] = [];
  const overflow: Suggestion[] = [];
  for (const s of out) {
    const k = s.family ?? s.name;
    const n = perFamily.get(k) ?? 0;
    if (n < 2) { perFamily.set(k, n + 1); spread.push(s); } else overflow.push(s);
  }
  return [...spread, ...overflow].slice(0, limit);
}

/**
 * Families that are a component rather than a way of joining two boards.
 *
 * Only consulted when the part number says nothing, to break what would
 * otherwise be an exact tie on the pin count alone.
 */
const DEVICE_FIRST = new Set([
  'DIP', 'SOIC', 'TSSOP', 'SSOP', 'QFP', 'PLCC', 'SOJ', 'TO', 'SOT', 'PAK',
  'SOD', 'DO', 'LED', 'chip', 'axial', 'radial',
]);

/**
 * What a reference designator says the part is.
 *
 * The prefixes are the IEEE 315 ones every schematic uses, and they carry more
 * about the *kind* of package than the part number usually does: `C4` is a
 * capacitor whatever it is called, and a capacitor is a chip or a radial can,
 * never a TO-3.
 */
export function designatorKind(
  ref: string,
): { what: string; families: string[] } | null {
  const p = /^([A-Za-z]+)/.exec(ref.trim());
  if (!p) return null;
  switch (p[1]!.toUpperCase()) {
    case 'R': case 'RN': case 'RV': case 'VR':
      return { what: 'a resistor', families: ['chip', 'axial'] };
    // `CN` is not here, though a capacitor network is one thing it can mean.
    // It far more often means a connector, it is claimed below, and a label
    // that could steer a suggestion either way is worth less than no label.
    case 'C':
      return { what: 'a capacitor', families: ['chip', 'radial'] };
    case 'L': case 'FB':
      return { what: 'an inductor', families: ['chip', 'axial', 'radial'] };
    case 'D': case 'LED': case 'CR': case 'ZD':
      return { what: 'a diode', families: ['SOD', 'DO', 'LED', 'axial'] };
    case 'Q': case 'T':
      return { what: 'a transistor', families: ['SOT', 'TO', 'PAK'] };
    case 'U': case 'IC': case 'A':
      return {
        what: 'an integrated circuit',
        families: ['DIP', 'SOIC', 'TSSOP', 'SSOP', 'QFP', 'PLCC', 'SOJ'],
      };
    case 'J': case 'P': case 'CN': case 'CON': case 'TB': case 'JP':
      return { what: 'a connector', families: ['IDC', 'SIP', 'D-sub'] };
    case 'SW': case 'S': case 'BT':
      return { what: 'a switch or a button', families: ['SIP', 'radial'] };
    case 'Y': case 'X': case 'XT':
      return { what: 'a crystal', families: ['radial', 'axial', 'chip'] };
    default:
      return null;
  }
}
